import React, { useState, useEffect, useContext, memo } from "react";
import { Card } from "react-bootstrap";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Alert, BackButton, ActionButton, DataStatus } from "components";
import { RABContextProvider, RABContext } from "../RABContext";
import { FormRAB, InfoSection, TableBarangJadi } from "../Section";
import { RABApi } from "api";

const TambahRAB = ({ setNavbarTitle }) => {
  const history = useHistory();
  const { id_rae } = useParams();
  const { state } = useLocation();

  const ContentTambahRAB = memo(() => {
    const { setType, dataAnalisaBarangJadi, setDataAnalisaBarangJadi } =
      useContext(RABContext);
    const [dataRAE, setDataRAE] = useState({});
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [isFetchingFailed, setIsFetchingFailed] = useState(false);
    const [dataBarangJadi, setDataBarangJadi] = useState([]);
    const [alertConfig, setAlertConfig] = useState({
      show: false,
      variant: "primary",
      text: "",
    });

    const getInitialData = () => {
      setIsPageLoading(true);

      RABApi.getSingleRAE(id_rae)
        .then((rae) => {
          // console.log(rae?.data?.data?.detail);
          const mapDataBarangJadi = rae?.data?.data?.detail?.map((val) =>
            Object({
              ...val,
              satuan: val.nama_satuan_jual,
              qty: val.qty ?? 0,
              harga_satuan: val.harga_satuan_rae ?? 0,
              harga_profit: val.harga_satuan_rae ?? 0,
              rounded: val.harga_satuan_rae ?? 0,
              unique: Math.random().toString(36).substring(2, 9),
            })
          );

          setDataRAE(rae?.data?.data ?? {});
          setDataBarangJadi(mapDataBarangJadi ?? []);
        })
        .catch((err) => {
          setIsFetchingFailed(true);
          setAlertConfig({
            show: true,
            variant: "danger",
            text: "Data gagal dimuat!",
          });
        })
        .finally(() => setIsPageLoading(false));
    };

    const formInitialValues = {
      id_rae: id_rae,
      id_peluang: id_rae,
      tgl_rab: "",
      no_rab: "",
      profit: 0,
    };
    const formValidationSchema = Yup.object().shape({
      tgl_rab: Yup.string().required("Pilih tanggal RAB"),
      no_rab: Yup.string().required("Pilih tanggal untuk menentukan nomor RAB"),
    });
    const formSubmitHandler = (values) => {
      const mapAnalisaBarangJadi = dataAnalisaBarangJadi?.map((item) => ({
        unique: item.unique,
        analisa: item?.analisa?.map((val) =>
          Object({
            id_item_buaso: val.id_item_buaso,
            id_item_atribut: val.id_item_atribut,
            id_buaso: val.id_buaso,
            qty: val.qty,
            harga_satuan: val.harga,
          })
        ),
      }));

      const mapBarangJadi = dataBarangJadi?.map((item) => ({
        id_item_atribut: item.id_item_atribut,
        id_peluang_detail: item.id_peluang_detail,
        id_barang_jadi: item.id_barang_jadi,
        qty_rab: item.qty ?? 0,
        unit_cost: item.total_analisa ?? 0,
        rounded: item.rounded,
        analisa_barang_jadi:
          mapAnalisaBarangJadi?.find((val) => item.unique === val.unique)
            ?.analisa ?? [],
      }));

      const finalValues = {
        ...values,
        detail_barang_jadi: mapBarangJadi,
      };

      RABApi.save(finalValues)
        .then(() =>
          history.push("/transaksi/rab/list-peluang", {
            ...state,
            alert: {
              show: true,
              variant: "primary",
              text: "Data berhasil ditambah!",
            },
          })
        )
        .catch(() => {
          setAlertConfig({
            show: true,
            type: "danger",
            text: "Data gagal ditambah!",
          });
        });
    };
    const formik = useFormik({
      enableReinitialize: true,
      initialValues: formInitialValues,
      validationSchema: formValidationSchema,
      onSubmit: formSubmitHandler,
    });

    const onProfitChange = (profit) => {
      const newProfit = profit ? profit : 0;

      const newList = dataBarangJadi?.map((val) => {
        const afterProfit = (newProfit / 100 + 1) * val.total_analisa;
        return {
          ...val,
          harga_profit: parseInt(afterProfit),
          rounded: parseInt(afterProfit),
        };
      });

      formik.setFieldValue("profit", profit);
      setDataBarangJadi(newList);
    };

    const onInputRoundedChange = ({ value, index }) => {
      const newList = dataBarangJadi.map((val, idx) =>
        idx === index ? { ...val, rounded: value } : val
      );

      setDataBarangJadi(newList);
    };

    useEffect(() => {
      setNavbarTitle("Rencana Anggaran Biaya (RAB)");
      getInitialData();
      setType("CREATE");
    }, []);

    return (
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <b>Tambah Data RAB</b>
          <BackButton
            onClick={() =>
              history.push("/transaksi/rab/list-peluang", { ...state })
            }
          />
        </Card.Header>
        <Card.Body>
          <Alert
            showCloseButton
            show={alertConfig.show}
            variant={alertConfig.variant}
            text={alertConfig.text}
            onClose={() => setAlertConfig({ ...alertConfig, show: false })}
          />
          {isPageLoading ? (
            <DataStatus loading={true} text="Memuat data . . ." />
          ) : isFetchingFailed ? (
            <DataStatus text="Data gagal dimuat, tidak dapat menambah data!" />
          ) : (
            <>
              <InfoSection data={dataRAE} />
              <hr />
              <FormRAB formik={formik} type="CREATE" />
              <hr />
              <TableBarangJadi
                dataBarangJadi={dataBarangJadi}
                setDataBarangJadi={setDataBarangJadi}
                profit={formik.values.profit}
                onProfitChange={onProfitChange}
                onInputRoundedChange={onInputRoundedChange}
                action="CREATE"
              />
              <div className="mt-4 d-flex justify-content-end align-items-center">
                <ActionButton
                  text="Tambah Rencana Anggaran Biaya"
                  loading={formik.isSubmitting}
                  onClick={formik.handleSubmit}
                />
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    );
  });

  return (
    <RABContextProvider>
      <ContentTambahRAB />
    </RABContextProvider>
  );
};

export default TambahRAB;
